import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

/** In dev, use Vite proxy so API calls are same-origin (httpOnly cookies). */
function apiOrigin(): string {
  if (import.meta.env.DEV) {
    return '';
  }
  return (import.meta.env.VITE_API_BASE_URL || window.location.origin).replace(/\/$/, '');
}

const baseURL = `${apiOrigin()}/api`;

export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

function urlPath(config: InternalAxiosRequestConfig): string {
  const u = config.url || '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

function isAuthRefresh(config: InternalAxiosRequestConfig): boolean {
  return urlPath(config).includes('/auth/refresh');
}

function isAuthLogin(config: InternalAxiosRequestConfig): boolean {
  return urlPath(config).includes('/auth/login');
}

function isAuthMe(config: InternalAxiosRequestConfig): boolean {
  return urlPath(config).includes('/auth/me');
}

let refreshing = false;
let waiters: Array<() => void> = [];

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    if (!original || error.response?.status !== 401) {
      return Promise.reject(error);
    }
    if (original._retry || isAuthRefresh(original) || isAuthLogin(original)) {
      return Promise.reject(error);
    }

    original._retry = true;

    if (refreshing) {
      await new Promise<void>((resolve) => waiters.push(resolve));
      return api(original);
    }

    refreshing = true;
    try {
      await refreshClient.post('/auth/refresh', {});
      waiters.forEach((w) => w());
      waiters = [];
      return api(original);
    } catch {
      waiters.forEach((w) => w());
      waiters = [];
      if (!isAuthMe(original)) {
        window.location.href = '/sign-in';
      }
      return Promise.reject(error);
    } finally {
      refreshing = false;
    }
  },
);

export default api;
